import { FC, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  CutsheetCatalog,
  Trim,
  TrimRankingQuestion,
} from '../../../types';
import { DropZone } from './drop-zone';
import { SortableItem } from './sortable-item';

export interface TrimRankingContentProps {
  question: TrimRankingQuestion;
  catalog: CutsheetCatalog;
  canAccept: boolean | null;
  draggingType: string | null;
  onAddItem?: (questionId: string, itemId: string) => void;
  onRemoveItem?: (questionId: string, itemId: string) => void;
  onEditItem?: (itemId: string, itemType: 'cut' | 'trim' | 'extra') => void;
  onReorderItems?: (questionId: string, newOrderIds: string[]) => void;
  onAddClick: () => void;
}

/**
 * Content panel for a TrimRanking question.
 *
 * Lists the available trims (with edit + remove actions) and a DropZone for
 * adding more via drag or click.
 * @param root0
 * @param root0.question
 * @param root0.catalog
 * @param root0.canAccept
 * @param root0.draggingType
 * @param root0.onAddItem
 * @param root0.onRemoveItem
 * @param root0.onEditItem
 * @param root0.onAddClick
 */
export const TrimRankingContent: FC<TrimRankingContentProps> = ({
  question,
  catalog,
  canAccept,
  draggingType,
  onAddItem,
  onRemoveItem,
  onEditItem,
  onReorderItems,
  onAddClick,
}) => {
  const trimRankingItems = useMemo(
    () =>
      (question?.availableTrimIds ?? [])
        .map((id) => catalog.trim.find((trim) => trim.id === id))
        .filter(Boolean) as Trim[],
    [question?.availableTrimIds, catalog.trim],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderItems) return;
    const ids = question.availableTrimIds ?? [];
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderItems(question.id, arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <Stack spacing={1}>
      {trimRankingItems.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: 'block' }}
          >
            Available Trims ({trimRankingItems.length}):
          </Typography>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleReorder}
          >
            <SortableContext
              items={trimRankingItems.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={0.5}>
                {trimRankingItems.map((trim) => (
                  <SortableItem
                    key={trim.id}
                    item={trim}
                    itemType="trim"
                    onEdit={
                      onEditItem ? () => onEditItem(trim.id, 'trim') : undefined
                    }
                    onRemove={
                      onRemoveItem
                        ? () => onRemoveItem(question.id, trim.id)
                        : undefined
                    }
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {onAddItem && (
        <DropZone
          droppableId={`question-dropzone-${question.id}`}
          droppableData={{ type: 'question-dropzone', questionId: question.id }}
          canAccept={canAccept}
          draggingType={draggingType}
          incompatibleMessage="This question only accepts trims"
          placeholder="Drop or click to add trims for ranking"
          onClick={onAddClick}
        />
      )}
    </Stack>
  );
};
